import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Building2, Eye, Edit, Archive, RotateCcw, Loader2,
  ArrowUpDown, ArrowUp, ArrowDown, Search, Filter, Plus, RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ModernLoading } from '@/components/common/ModernPageTemplate';
import { useDepartments, useArchiveDepartment, useRestoreDepartment } from '@/hooks/useDepartments';
import { departmentAPI } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import DepartmentFilters from './DepartmentFilters';
import CreateDepartmentModal from './CreateDepartmentModal';
import EditDepartmentModal from './EditDepartmentModal';
import DepartmentDetailsDrawer from './DepartmentDetailsDrawer';

const DEFAULT_FILTERS = { status: '', owner: '' };
const ROWS_OPTIONS = [5, 10, 20, 30, 50];

const useCompanyUsers = () =>
  useQuery({
    queryKey: ['department-company-users'],
    queryFn: async () => {
      const res = await departmentAPI.getCompanyUsers();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

const StatusBadge = ({ status }) =>
  status === 'active' ? (
    <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-0 text-xs">Active</Badge>
  ) : (
    <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 border-0 text-xs">Archived</Badge>
  );

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
};

const DepartmentList = () => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [drawerDeptId, setDrawerDeptId] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const archiveMutation = useArchiveDepartment();
  const restoreMutation = useRestoreDepartment();

  const { data: companyUsersData } = useCompanyUsers();
  const companyUsers = Array.isArray(companyUsersData) ? companyUsersData : [];

  useEffect(() => { setPage(1); }, [search]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortOrder('asc'); }
    setPage(1);
  };

  const queryParams = {
    page, limit: rowsPerPage,
    search: search || undefined,
    status: filters.status || undefined,
    owner: filters.owner || undefined,
    sortBy, sortOrder,
  };

  const { data, isLoading, isError, refetch } = useDepartments(queryParams);

  const departments = data?.departments || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const handleArchive = (dept) => {
    setPendingId(dept._id);
    archiveMutation.mutate(dept._id, { onSettled: () => setPendingId(null) });
  };

  const handleRestore = (dept) => {
    setPendingId(dept._id);
    restoreMutation.mutate(dept._id, { onSettled: () => setPendingId(null) });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setSearch(''); setFilters(DEFAULT_FILTERS); setPage(1);
    setSortBy('createdAt'); setSortOrder('desc');
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Page number buttons (max 5 visible)
  const pageButtons = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
    if (totalPages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= totalPages - 2) return totalPages - 4 + i;
    return page - 2 + i;
  });

  return (
    <>
      {/* Full-height flex column layout */}
      <div className="admin-page-container admin-fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* ── Header ── */}
        <div className="admin-page-header">
          <div className="admin-header-top">
            <h1 className="admin-page-title">Departments</h1>
            <div className="flex items-center gap-3">
              <span className="admin-stat-chip admin-stat-total">Total: {total}</span>
            </div>
          </div>
          <div className="admin-header-bottom">
            <div className="admin-actions-section" style={{ marginLeft: 'auto' }}>
              {/* Search */}
              <div className="admin-header-search">
                <div className="admin-search-input-wrapper-header">
                  <input
                    type="search"
                    placeholder="Search departments..."
                    className="admin-search-input-header"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Search className="w-4 h-4 admin-search-icon-header-clickable" />
                </div>
              </div>
              {/* Filter */}
              <button
                className={`admin-action-btn border rounded px-2 py-1 ${showFilters || filters.status || filters.owner ? 'admin-filter-active' : ''}`}
                onClick={() => setShowFilters((v) => !v)}
                title="Filter"
              >
                <Filter className="h-4 w-4" />
              </button>
              {/* Create */}
              <button
                className="admin-action-btn border rounded px-2 py-1"
                onClick={() => setCreateOpen(true)}
                title="Create Department"
              >
                <Plus className="h-4 w-4" />
              </button>
              {/* Refresh */}
              <button
                className="admin-action-btn border rounded px-2 py-1"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Scrollable content area (grows to fill space) ── */}
        <div className="admin-content-area" style={{ flex: 1, overflowY: 'auto', paddingBottom: 0 }}>
          {showFilters && (
            <DepartmentFilters
              filters={filters}
              onFilterChange={(f) => { setFilters(f); setPage(1); }}
              companyUsers={companyUsers}
            />
          )}

          {isLoading ? (
            <ModernLoading text="Loading departments..." />
          ) : isError ? (
            <div className="text-center p-6 text-red-500 text-sm">Failed to load departments. Please try again.</div>
          ) : departments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Building2 className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-base font-medium">No departments found</p>
              <p className="text-sm mt-1">
                {search || filters.status || filters.owner
                  ? 'Try adjusting your search or filters'
                  : 'Create your first department to get started'}
              </p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">S.No</th>
                    <th className="admin-table-cell sortable cursor-pointer select-none" onClick={() => handleSort('name')}>
                      <div className="flex items-center">Name <SortIcon field="name" sortBy={sortBy} sortOrder={sortOrder} /></div>
                    </th>
                    <th className="admin-table-cell">Code</th>
                    <th className="admin-table-cell">Owner</th>
                    <th className="admin-table-cell">Members</th>
                    <th className="admin-table-cell">Status</th>
                    <th className="admin-table-cell sortable cursor-pointer select-none" onClick={() => handleSort('createdAt')}>
                      <div className="flex items-center">Created <SortIcon field="createdAt" sortBy={sortBy} sortOrder={sortOrder} /></div>
                    </th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept, idx) => {
                    const isRowPending = pendingId === dept._id;
                    return (
                      <tr key={dept._id} className="admin-table-row">
                        <td className="admin-table-cell number">{(page - 1) * rowsPerPage + idx + 1}</td>
                        <td className="admin-table-cell font-medium">
                          <div className="flex items-center gap-2">
                            {dept.color && <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />}
                            <span className="text-sm">{dept.name}</span>
                          </div>
                        </td>
                        <td className="admin-table-cell">
                          {dept.code
                            ? <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">{dept.code}</span>
                            : <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="admin-table-cell text-sm">
                          {dept.owner?.name || <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="admin-table-cell text-sm">{dept.memberCount ?? dept.members?.length ?? 0}</td>
                        <td className="admin-table-cell"><StatusBadge status={dept.status} /></td>
                        <td className="admin-table-cell text-xs">
                          {dept.createdAt ? format(new Date(dept.createdAt), 'MMM dd, yy') : '—'}
                        </td>
                        <td className="admin-table-cell text-right">
                          <div className="admin-table-actions">
                            <button className="admin-table-action-btn" onClick={() => setDrawerDeptId(dept._id)} title="View">
                              <Eye className="h-3 w-3" />
                            </button>
                            {dept.status === 'active' && (
                              <button className="admin-table-action-btn edit" onClick={() => setEditDept(dept)} title="Edit">
                                <Edit className="h-3 w-3" />
                              </button>
                            )}
                            {dept.status === 'active' ? (
                              <button className="admin-table-action-btn delete" onClick={() => handleArchive(dept)} disabled={isRowPending} title="Archive">
                                {isRowPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
                              </button>
                            ) : (
                              <button className="admin-table-action-btn create" onClick={() => handleRestore(dept)} disabled={isRowPending} title="Restore">
                                {isRowPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Footer — always at bottom ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '10px 20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          flexShrink: 0,
          fontSize: '13px',
          color: '#374151',
        }}>
          {/* Left: Rows per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(1); }}
              style={{
                border: '1px solid #d1d5db', borderRadius: '4px',
                padding: '4px 8px', fontSize: '12px',
                backgroundColor: '#ffffff', cursor: 'pointer', minWidth: '60px',
              }}
            >
              {ROWS_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {/* Center: Page navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: '4px 12px', borderRadius: '4px', fontSize: '12px',
                border: '1px solid #d1d5db',
                backgroundColor: page <= 1 ? '#f3f4f6' : '#fff',
                color: page <= 1 ? '#9ca3af' : '#374151',
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
              }}
            >‹ Prev</button>

            {pageButtons.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                style={{
                  width: '30px', height: '28px', borderRadius: '4px', fontSize: '12px',
                  border: page === pageNum ? '1.5px solid #16a34a' : '1px solid #d1d5db',
                  backgroundColor: page === pageNum ? '#16a34a' : '#fff',
                  color: page === pageNum ? '#fff' : '#374151',
                  fontWeight: page === pageNum ? 600 : 400,
                  cursor: 'pointer',
                }}
              >{pageNum}</button>
            ))}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                padding: '4px 12px', borderRadius: '4px', fontSize: '12px',
                border: '1px solid #d1d5db',
                backgroundColor: page >= totalPages ? '#f3f4f6' : '#fff',
                color: page >= totalPages ? '#9ca3af' : '#374151',
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >Next ›</button>
          </div>

          {/* Right: Go to + Total */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Go to:</span>
              <select
                value={page}
                onChange={(e) => setPage(parseInt(e.target.value))}
                style={{
                  border: '1px solid #d1d5db', borderRadius: '4px',
                  padding: '3px 6px', fontSize: '12px',
                  backgroundColor: '#fff', cursor: 'pointer', minWidth: '52px',
                }}
              >
                {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>
            <span style={{ fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>Total: {total}</span>
          </div>
        </div>
      </div>

      <CreateDepartmentModal open={createOpen} onClose={() => setCreateOpen(false)} companyUsers={companyUsers} />
      <EditDepartmentModal open={!!editDept} onClose={() => setEditDept(null)} department={editDept} companyUsers={companyUsers} />
      <DepartmentDetailsDrawer open={!!drawerDeptId} onClose={() => setDrawerDeptId(null)} departmentId={drawerDeptId} companyUsers={companyUsers} />
    </>
  );
};

export default DepartmentList;
